import type { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { Button, Field, Link } from '@cluey/cluey-components';
import { useMutation } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import Cookies from 'js-cookie';
import get from 'lodash/get';
import { FC, FormEvent, PropsWithChildren, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { z } from 'zod';

import { getCredentials, loginSuccess } from '../../../actions/hub/hubAuthPagesActions';
import { emailVerifiedSuccess, requestToResetPassword } from '../../../actions/hub/hubAuthPagesActions';
import { ReactComponent as ClueyPlusLogo } from '../../../assets/icons/cluey-plus-logo.svg';
import iconGG from '../../../assets/images/google.svg';
import withHeadTag from '../../../common/HeadComponent';
import { useVerifyEmail } from '../../../hooks/queries/useVerifyEmail';
import { ArrowBackIcon } from '../../../icons';
import { logError } from '../../../logging';
import { logIn } from '../../../services/awsAuth';
import { CUSTOMER_CARE_CONTACT_PHONE_2 } from '../../../util/constants';
import * as PATHS from '../../../util/pagePath';
import { usePostHogCapture } from '../../../util/posthog';
import RightColumn from './RightColumn';
import { useSignupStore } from './SignUp';
import TwoColumnLayout from './TwoColumnLayout';
import { Auth2Layout, InlineLoader, useRedirectIfAuthenticated } from './common';
import useNavigateWithSearch from './useNavigateWithSearch';

const ICON_SIZE = 16;

export const LoginWith: FC<PropsWithChildren<{ onClick: () => void; icon: string; alt: string }>> = ({
  children,
  icon,
  alt,
  onClick,
}) => {
  return (
    <button
      className="flex w-full items-center rounded border border-grey-2 ring-blue-2 hover:bg-slate-1 focus:ring"
      onClick={onClick}
    >
      <div className="p-3">
        <img src={icon} height={ICON_SIZE} width={ICON_SIZE} alt={alt} />
      </div>
      <div className="flex h-full flex-grow items-center border-l border-grey-2 p-3 text-xs font-bold uppercase tracking-widest text-grey-6">
        {children}
      </div>
    </button>
  );
};

const LoginPageValidation = z.object({
  email: z.string().email('Please enter a valid email address.').min(1, 'Please enter your email address.'),
  password: z.string().min(1, 'Please enter your password.'),
});

const EmailOnlyValidation = LoginPageValidation.omit({ password: true });

const AUTH_ERROR_MSG = {
  NO_USER: "We don't have an account associated with this email, please try again.",
  INCORRECT: 'Incorrect username or password.',
};

interface EmailValidationResponse {
  exists: boolean;
  enabled: boolean;
  status:
    | 'UNCONFIRMED'
    | 'CONFIRMED'
    | 'ARCHIVED'
    | 'COMPROMISED'
    | 'UNKNOWN'
    | 'RESET_REQUIRED'
    | 'FORCE_CHANGE_PASSWORD'
    | 'EXTERNAL_PROVIDER';

  username: string;
}

const SSO_ERROR_MESSAGE = 'We could not complete sign-in from Student Hub. Please log in with your email and password.';

const LoginPageComponent = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const continueChildSignup = params.get('continueChildSignup') === 'true';
  const ssoError = params.get('ssoError');
  const navigateWithSearch = useNavigateWithSearch();
  console.log('[debug] continueChildSignup:', { continueChildSignup, params });

  useRedirectIfAuthenticated();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [notifyActivationNeeded, setNotifyActivationNeeded] = useState(false);
  const [notifyPasswordReset, setNotifyPasswordReset] = useState(false);
  const [promptForPassword, setPromptForPassword] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    root: ssoError ? SSO_ERROR_MESSAGE : '',
  });
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();

  const capture = usePostHogCapture();

  const fedAuthSignIn = (provider: `${CognitoHostedUIIdentityProvider}`) => {
    console.log('[debug] socialOauthSourcePage:', location.pathname);
    Cookies.set('socialOauthSourcePage', location.pathname);
    Cookies.set('socialOauthSource', provider);
    capture('click_auth-log-in', { provider });
    Auth.federatedSignIn({ provider: provider as CognitoHostedUIIdentityProvider });
  };

  const { mutate: verifyEmail } = useVerifyEmail({
    email,
    onSuccess: async (response: EmailValidationResponse) => {
      if (!response.exists) {
        navigateWithSearch(PATHS.PATH_HUB_SIGN_UP, { loginPageEmail: email });
      } else if (response.enabled && response.status === 'CONFIRMED') {
        dispatch(emailVerifiedSuccess(email));
        setPromptForPassword(true);
      } else if (response.status === 'EXTERNAL_PROVIDER') {
        navigateWithSearch(`${PATHS.PATH_HUB_SIGN_UP}?oauthSignup=true&email=${email}`);
        // dispatch(emailVerifiedSuccess(email));
        // setPromptForPassword(true);
        //
        // setErrors((curr) => ({
        //   ...curr,
        //   email: 'Please login with your external provider (Google or Facebook) using the buttons below.',
        // }));
      } else if (response.status === 'FORCE_CHANGE_PASSWORD' || response.status === 'RESET_REQUIRED') {
        dispatch(
          requestToResetPassword({
            email,
            onSuccess: () => {
              setNotifyPasswordReset(true);
              setLoading(false);
            },
            onError: () => {
              setErrors((curr) => ({
                ...curr,
                email: 'Failed to verify email. Please try again.',
              }));
              setLoading(false);
            },
          })
        );
      } else {
        useSignupStore.setState({
          username: response.username,
          email,
        });
        await Auth.resendSignUp(response.username);
        navigateWithSearch(`${PATHS.PATH_HUB_SIGN_UP}verify`);
      }
      // If the user has to reset their password, we need to keep the loading state until the user resets their password
      setLoading(response.status !== 'FORCE_CHANGE_PASSWORD' ? false : true);
    },
    onError: () => {
      setErrors((curr) => ({
        ...curr,
        email: 'Failed to verify email. Please try again.',
      }));
      setLoading(false);
    },
  });

  const { mutate: signIn } = useMutation({
    mutationFn: async (params: { email: string; password: string }) => {
      capture('click_auth-log-in', { provider: 'email' });
      try {
        await logIn(params);
        dispatch(getCredentials()).then(() => {
          navigateWithSearch(PATHS.PATH_HUB_HOME);
          dispatch(loginSuccess(email));
        });
        return true;
      } catch (error) {
        let errorMsg = get(error, 'message', '');
        if (errorMsg === 'User is disabled.') {
          errorMsg = `Your login doesn't appear to be working, please contact Customer Care on ${CUSTOMER_CARE_CONTACT_PHONE_2}.`;
        } else if (errorMsg === 'User does not exist.') {
          errorMsg = "We don't have an account associated with this email, please try again.";
        }

        setErrors((curr) => ({
          ...curr,
          root: errorMsg,
        }));
        logError(errorMsg, null, {
          email,
          message: `User using email ${email} failed to log in`,
        });
        return false;
      }
    },
    onSettled: (result: boolean) => {
      if (!result) {
        setLoading(false);
      }
    },
    onMutate: () => {
      setLoading(true);
    },
  });

  const handleValidateEmail = async (email: string) => {
    const result = EmailOnlyValidation.safeParse({ email });
    if (result.success === true) {
      setLoading(true);
      verifyEmail(email);
    } else {
      setErrors((curr) => ({
        ...curr,
        email: 'Please enter a valid email address.',
      }));
    }
  };

  const handleLogin = () => {
    const result = LoginPageValidation.safeParse({ email, password });

    if (result.success === true) {
      // sign in
      signIn({ email, password });
    } else {
      const errorFields = {
        email: '',
        password: '',
        root: '',
      };

      result.error.errors.forEach((error) => {
        const field = error.path.join('.');
        errorFields[field] = error.message;
      });

      setErrors(errorFields);
      setLoading(false);
    }
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({ email: '', password: '', root: '' });

    if (promptForPassword) {
      handleLogin();
    } else {
      handleValidateEmail(email);
    }
  };

  const submitLabel = promptForPassword ? 'Log in' : 'Continue';

  if (notifyActivationNeeded) {
    return (
      <Auth2Layout
        title="Check your email"
        headerLinks={
          <div className="flex justify-between">
            <button
              onClick={() => setNotifyActivationNeeded(false)}
              className="flex items-center justify-between gap-1"
            >
              <ArrowBackIcon width={ICON_SIZE} height={ICON_SIZE} className="text-blue-5" />
              <span className="underline-offset-3 text-lg text-blue-5 underline">Back</span>
            </button>
          </div>
        }
      >
        <p className="pb-8">
          We have sent a link to <strong className="font-bold">{email || 'your email'}</strong> to activate your
          account.
        </p>

        <p>Check your spam or junk folder if you don't see the email.</p>
      </Auth2Layout>
    );
  }

  if (notifyPasswordReset) {
    return (
      <Auth2Layout
        title="Check your email"
        headerLinks={
          <div className="flex justify-between">
            <button onClick={() => setNotifyPasswordReset(false)} className="flex items-center justify-between gap-1">
              <ArrowBackIcon width={ICON_SIZE} height={ICON_SIZE} className="text-blue-5" />
              <span className="underline-offset-3 text-lg text-blue-5 underline">Back</span>
            </button>
          </div>
        }
      >
        <p className="pb-8">
          Your password needs to be reset. We have sent a link to{' '}
          <strong className="font-bold">{email || 'your email'}</strong> to reset your password.
        </p>

        <p>Check your spam or junk folder if you don't see the email.</p>
      </Auth2Layout>
    );
  }

  const showGoogleOption = false;

  const renderLeftColumn = () => (
    <div className="ml-auto mr-auto flex h-full max-w-[420px] flex-col self-center px-5 pt-[140px]">
      <div>
        <ClueyPlusLogo className="mb-6 h-[41px] w-[140px]" />
      </div>
      <h1 className="max-w-md pb-9 font-display text-[28px] leading-10 sm:text-[32px]">Log in or sign up to Cluey</h1>
      {/* Login with Google */}
      {showGoogleOption && (
        <>
          <LoginWith icon={iconGG} alt="google" onClick={() => fedAuthSignIn('Google')}>
            Continue with Google
          </LoginWith>
          <div className="flex w-full max-w-sm items-center py-10">
            <div className="h-px flex-grow bg-slate-2"></div>
            <p className="px-4">Or</p>
            <div className="h-px flex-grow bg-slate-2"></div>
          </div>
        </>
      )}

      <div className="flex w-full max-w-sm flex-col items-start gap-4">
        {errors.root && errors.root !== AUTH_ERROR_MSG.NO_USER && errors.root !== AUTH_ERROR_MSG.INCORRECT && (
          <p className="w-full max-w-sm text-sm text-red-5" role="alert">
            {errors.root}
          </p>
        )}
        <form onSubmit={onSubmit} className="flex w-full flex-col items-start gap-6">
          <div>
            <Field
              label="Email"
              name="email"
              value={email}
              onChange={(e) => {
                setErrors({ ...errors, email: '' });
                setEmail(e);
              }}
              type="email"
              className="w-full max-w-sm"
              error={errors.email || (errors.root === AUTH_ERROR_MSG.NO_USER && errors.root)}
              disabled={loading}
            />
            <div className="text-gray-5 text-sm">We will create an account for you if you don't have one yet</div>
          </div>
          {promptForPassword && (
            <Field
              label="Password"
              name="password"
              value={password}
              onChange={(e) => {
                setErrors({ ...errors, password: '' });
                setPassword(e);
              }}
              type="password"
              className="w-full max-w-sm"
              error={errors.password || (errors.root === AUTH_ERROR_MSG.INCORRECT && errors.root)}
              disabled={loading}
            />
          )}
          <Button size="lg" type="submit" disabled={loading} className="flex w-full max-w-sm flex-row justify-center">
            {!loading ? submitLabel : <InlineLoader />}
          </Button>
          {promptForPassword && (
            <p>
              Forgot your password?{' '}
              <Link size="base" to={PATHS.PATH_HUB_RESET_PASSWORD_REQUEST} className="link">
                Reset password
              </Link>
            </p>
          )}
        </form>
      </div>
    </div>
  );

  const renderRightColumn = () => <RightColumn step={1} />;

  return <TwoColumnLayout renderLeftColumn={renderLeftColumn} renderRightColumn={renderRightColumn} />;
};

export const LoginPage = withHeadTag(LoginPageComponent);
